import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PlatformSalaryService } from '../../../../Component/Api/PlatformSalary/PlatformSalaryService';
import { ConfirmationDialog } from '../../../../Component/Common/Components/ConfirmationDialog';
import { DataTable } from '../../../../Component/Common/Components/DataTable/DataTable';
import { PlatformSalary } from '../../../../Component/Model/Dto/Platform/PlatformSalary';
import { HttpStatusCode } from '../../../../Component/Model/Enum/HttpStatusCode';
import { PlatformSalaryModal } from './PlatformSalaryModal';
import { PlatformSalaryTableMapper } from './PlatformSalaryTable';
import { PlatformPeopleSalaryEditRole, UserAdminRole } from '../../../../Component/Common/Constants/UserRoles';
import { useRecoilValue } from 'recoil';
import { userRolesState } from '../../../../State/user.Atom';
import { IBulkActions } from '../../../../Component/Model/Table/Table';

interface IProps {
  personId: string;
  personFullName?: string;
}

export const PlatformSalaryDataPage = (props: IProps) => {
  const userRoles = useRecoilValue(userRolesState);
  const [salaryData, setSalaryData] = useState<PlatformSalary.PlatformSalarySummaryDto[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showPlatformSalaryModal, setShowPlatformSalaryModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [selectedYear, setSelectedYear] = useState<number>();
  const confirmDeleteMessage = `Are you sure you want to delete the salary year ${selectedYear} for ${props.personFullName}?`;

  const editRoleAccess = [
    PlatformPeopleSalaryEditRole,
    UserAdminRole
]

const isEditable = userRoles.some(r => editRoleAccess.includes(r));

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  }

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage(entriesPerPage);
    setPageNumber(1);
  }

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await PlatformSalaryService.getPersonSalarySummary(props.personId);
      setSalaryData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [props.personId]);

  const handleAddSalary = () => {
    setShowPlatformSalaryModal(true);
    setSelectedYear(undefined);
  }

  const handleEditForYear = (year: number) => {
    setSelectedYear(year);
    setShowPlatformSalaryModal(true);
  }

  const handleClosePlatformSalaryModal = () => {
    setShowPlatformSalaryModal(false);
    fetchData();
    setSelectedYear(undefined);
  }

  const handleDeleteForYear = (year: number) => {
    setSelectedYear(year);
    setShowConfirmationDialog(true);
  }

  const handleCloseConfirmationModal = async () => {
    setShowConfirmationDialog(false);
    await fetchData();
    setSelectedYear(undefined);
  }

  const deleteSalaryByYear = async () => {
    try {
      if (props.personId != undefined && selectedYear != undefined) {
        const response = await PlatformSalaryService.deleteSalaryByYear(props.personId, selectedYear);
        if (response.status === HttpStatusCode.OK) {
          toast.success(`Successfully deleted salary for ${selectedYear}`);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(`Unable to delete salary for ${selectedYear}`);
    } finally {
      handleCloseConfirmationModal();
    }
  }

  const bulkActions: IBulkActions[] = [
  ]

  if (isEditable) {
      bulkActions.push({
        label: "Capture Salary Year",
        action: handleAddSalary
      });
  }



  const indexOfLastPage = pageNumber * entriesPerPage;
  const indexOfFirstPage = indexOfLastPage - entriesPerPage;
  const itemCount = salaryData.length;

  const currentData = salaryData.slice(
    indexOfFirstPage,
    indexOfLastPage
  );

  const salaryTableData = PlatformSalaryTableMapper(currentData, handleEditForYear, handleDeleteForYear);

  return (
    <>
      <DataTable
        key={pageNumber}
        table={salaryTableData}
        entriesPerPage={{ defaultValue: entriesPerPage }}
        pageNumber={pageNumber}
        filteredItemsCount={itemCount}
        totalItemsCount={itemCount}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        isLoading={isLoading}
        bulkActions={bulkActions}
      />
      <ConfirmationDialog
        showDialog={showConfirmationDialog}
        handleAccept={deleteSalaryByYear}
        handleCancel={handleCloseConfirmationModal}
        heading={"Delete Salary Year"}
        message={confirmDeleteMessage}
      />

      {
        showPlatformSalaryModal &&
        <PlatformSalaryModal
          key={selectedYear}
          personId={props.personId}
          selectedYearForEdit={selectedYear}
          handleClose={handleClosePlatformSalaryModal}
          showModal={showPlatformSalaryModal}
        />
      }

    </>
  )
}